import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { usePlatformAdmin } from '../../hooks';
import styles from './Admin.module.css';
import { orgOptionLabels } from '../../lib/orgLabels';

export const Route = createFileRoute('/admin/plan-tiers')({
  component: PlanTiersRoute,
});

// Get API base URL (convert wss:// to https://)
function getApiBaseUrl(): string {
  const wsUrl = (import.meta as any).env.VITE_API_URL || 'wss://api.dev.interlucent.ai';
  return wsUrl.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:');
}

interface AdminOrg {
  id: string;
  name: string;
  orgSlug: string | null;
  planTierId: string | null;
  createdAt: string;
  deactivatedAt: string | null;
}

interface PlanTierData {
  id: string;
  tierSlug: string;
  tierName: string;
  quotaMaxProjects: number;
  quotaMaxAppsPerProject: number;
  quotaMaxVersionsPerApp: number;
  quotaMaxConcurrentStreams: number;
  quotaMaxLocalVersionsPerApp: number;
  quotaMaxCustomDomains: number;
  allowInstantOn: boolean | number;
  allowProfiles: boolean | number;
  updatedAt?: string;
}

const QUOTA_FIELDS: Array<{
  key: keyof Pick<PlanTierData,
    'quotaMaxProjects' | 'quotaMaxAppsPerProject' | 'quotaMaxVersionsPerApp' |
    'quotaMaxConcurrentStreams' | 'quotaMaxLocalVersionsPerApp' | 'quotaMaxCustomDomains'>;
  label: string;
  hint?: string;
}> = [
  { key: 'quotaMaxProjects', label: 'Max Projects' },
  { key: 'quotaMaxAppsPerProject', label: 'Max Applications per Project' },
  { key: 'quotaMaxVersionsPerApp', label: 'Max Versions per Application' },
  { key: 'quotaMaxConcurrentStreams', label: 'Max Concurrent Streams' },
  { key: 'quotaMaxLocalVersionsPerApp', label: 'Max Local Versions per Application' },
  { key: 'quotaMaxCustomDomains', label: 'Max Custom Domains', hint: '0 = none, -1 = unlimited' },
];

function PlanTiersRoute() {
  const { isPlatformAdmin } = usePlatformAdmin();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [orgs, setOrgs] = useState<AdminOrg[]>([]);
  const orgLabels = useMemo(() => orgOptionLabels(orgs.filter(o => !o.deactivatedAt)), [orgs]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [planTier, setPlanTier] = useState<PlanTierData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form fields
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [allowProfiles, setAllowProfiles] = useState(false);
  const [allowInstantOn, setAllowInstantOn] = useState(false);

  // Redirect non-admins
  useEffect(() => {
    if (!isPlatformAdmin) {
      navigate({ to: '/' });
    }
  }, [isPlatformAdmin, navigate]);

  const authHeaders = useCallback(async () => {
    const token = await getToken({ template: 'default' });
    if (!token) throw new Error('Authentication required');
    return { Authorization: `Bearer ${token}` };
  }, [getToken]);

  // Fetch orgs on mount
  useEffect(() => {
    if (!isPlatformAdmin) return;
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(`${getApiBaseUrl()}/api/admin/organizations`, { headers });
        if (!res.ok) return;
        const data = await res.json();
        const fetched: AdminOrg[] = (data as any).organizations ?? [];
        fetched.sort((a, b) => a.name.localeCompare(b.name));
        setOrgs(fetched);
      } catch { /* non-critical */ }
    })();
  }, [isPlatformAdmin, authHeaders]);

  // Fetch plan tier when org changes
  useEffect(() => {
    if (!selectedOrgId) {
      setPlanTier(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setSuccess(null);

    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(
          `${getApiBaseUrl()}/api/admin/organizations/${encodeURIComponent(selectedOrgId)}/plan-tier`,
          { headers }
        );
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error((data as any).error || `HTTP ${res.status}`);
        }
        if (cancelled) return;

        const data = await res.json() as { planTier: PlanTierData | null };
        const pt = (data as any).planTier;
        setPlanTier(pt);

        if (pt) {
          const vals: Record<string, string> = {};
          for (const f of QUOTA_FIELDS) {
            vals[f.key] = String(pt[f.key] ?? 0);
          }
          setFormValues(vals);
          setAllowProfiles(!!pt.allowProfiles);
          setAllowInstantOn(!!pt.allowInstantOn);
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Failed to fetch plan tier');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [selectedOrgId, authHeaders]);

  const handleSave = useCallback(async () => {
    if (!selectedOrgId || !planTier) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const headers = await authHeaders();
      const body: Record<string, unknown> = { allowProfiles, allowInstantOn };

      for (const f of QUOTA_FIELDS) {
        const val = parseInt(formValues[f.key], 10);
        if (!isNaN(val)) body[f.key] = val;
      }

      const res = await fetch(
        `${getApiBaseUrl()}/api/admin/organizations/${encodeURIComponent(selectedOrgId)}/plan-tier`,
        {
          method: 'PUT',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }

      const data = await res.json() as { planTier: PlanTierData };
      setPlanTier((data as any).planTier);
      setSuccess('Plan tier updated successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to update plan tier');
    } finally {
      setSaving(false);
    }
  }, [selectedOrgId, planTier, formValues, allowProfiles, allowInstantOn, authHeaders]);

  const hasChanges = useMemo(() => {
    if (!planTier) return false;
    for (const f of QUOTA_FIELDS) {
      if (String(planTier[f.key] ?? 0) !== formValues[f.key]) return true;
    }
    if (!!planTier.allowProfiles !== allowProfiles) return true;
    if (!!planTier.allowInstantOn !== allowInstantOn) return true;
    return false;
  }, [planTier, formValues, allowProfiles, allowInstantOn]);

  if (!isPlatformAdmin) return null;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>Plan Tiers</h1>
      </header>

      <div className={styles.searchBar}>
        <select
          className={styles.formSelect}
          value={selectedOrgId}
          onChange={e => setSelectedOrgId(e.target.value)}
          style={{ flex: 1 }}
        >
          <option value="">Select organization...</option>
          {orgs.filter(o => !o.deactivatedAt).map(o => (
            <option key={o.id} value={o.id}>{orgLabels.get(o.id) ?? o.name}</option>
          ))}
        </select>
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {success && <div className={styles.successMessage}>{success}</div>}

      {loading && <div className={styles.loading}>Loading plan tier...</div>}

      {!loading && selectedOrgId && !planTier && !error && (
        <div className={styles.empty}>No plan tier assigned to this organization</div>
      )}

      {!loading && planTier && (
        <div className={styles.planTierForm}>
          <div className={styles.planTierHeader}>
            <span className={styles.planTierName}>{planTier.tierName}</span>
            <span className={styles.planTierId}>{planTier.id}</span>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>Quotas</h3>
            <div className={styles.quotaGrid}>
              {QUOTA_FIELDS.map(f => (
                <div key={f.key} className={styles.quotaField}>
                  <label className={styles.formLabel}>{f.label}</label>
                  <input
                    className={styles.searchInput}
                    type="number"
                    value={formValues[f.key] ?? '0'}
                    onChange={e => setFormValues(prev => ({ ...prev, [f.key]: e.target.value }))}
                    disabled={saving}
                    style={{ width: 120 }}
                  />
                  {f.hint && <span className={styles.fieldHint}>{f.hint}</span>}
                </div>
              ))}
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>Feature Flags</h3>
            <div className={styles.featureFlagGrid}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={allowProfiles}
                  onChange={e => setAllowProfiles(e.target.checked)}
                  disabled={saving}
                />
                Allow Deployment Profiles
              </label>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={allowInstantOn}
                  onChange={e => setAllowInstantOn(e.target.checked)}
                  disabled={saving}
                />
                Allow Instant-On
              </label>
            </div>
          </div>

          <div className={styles.confirmActions}>
            <button
              className={styles.applyButton}
              onClick={handleSave}
              disabled={saving || !hasChanges}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
